/**
 * Pure helpers: decide what Entity Hub sections to render.
 * Show only real content — never empty placeholders or backend fallback stubs.
 */

function nonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0;
}

function nonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Drop backend stub copy like "Fallback entity for jogasimples".
 */
export function isUsefulCopy(text) {
  if (!nonEmptyString(text)) return false;
  const t = text.trim();
  if (/^fallback\b/i.test(t)) return false;
  if (/^fallback entity\b/i.test(t)) return false;
  if (/^n\/?a$/i.test(t)) return false;
  if (/^unknown$/i.test(t)) return false;
  return true;
}

export function usefulCuriosities(hub) {
  const list = hub?.curiosities;
  if (!nonEmptyArray(list)) return [];
  return list.filter((c) => isUsefulCopy(c?.text || c?.summary || ''));
}

export function usefulNews(hub) {
  const list = hub?.news;
  if (!nonEmptyArray(list)) return [];
  return list.filter(
    (n) => isUsefulCopy(n?.title) || isUsefulCopy(n?.summary)
  );
}

export function usefulConcerts(hub) {
  const list = hub?.concerts;
  if (!nonEmptyArray(list)) return [];
  return list.filter(
    (c) =>
      isUsefulCopy(c?.title) ||
      isUsefulCopy(c?.venueName) ||
      Boolean(c?.startsAt)
  );
}

function rowHasMeaningfulStats(row) {
  if (!row || typeof row !== 'object') return false;
  const pts = row.points;
  if (pts != null && String(pts).trim() !== '' && String(pts).trim() !== '0') {
    return true;
  }
  return [row.played, row.won, row.drawn, row.lost].some((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  });
}

export function hasNextEvent(hub) {
  const event = hub?.nextEvent;
  if (!event || typeof event !== 'object') return false;
  return Boolean(
    event.home?.name ||
      event.away?.name ||
      isUsefulCopy(event.title) ||
      event.startsAt
  );
}

export function hasStandings(hub) {
  return competitionsWithRows(hub).length > 0;
}

export function competitionsWithRows(hub) {
  const tables = hub?.standingsByCompetition;
  if (!nonEmptyArray(tables)) return [];
  return tables.filter((t) => {
    const rows = t?.rows;
    if (!nonEmptyArray(rows)) return false;
    // Hide all-zero / empty-stat tables (incomplete provider data).
    return rows.some(rowHasMeaningfulStats);
  });
}

export function hasStreamingPlatforms(hub) {
  return nonEmptyArray(hub?.streamingPlatforms);
}

export function hasLinks(hub) {
  if (!nonEmptyArray(hub?.links)) return false;
  return hub.links.some((l) => nonEmptyString(l?.url));
}

export function hasCuriosities(hub) {
  return usefulCuriosities(hub).length > 0;
}

export function hasNews(hub) {
  return usefulNews(hub).length > 0;
}

export function hasConcerts(hub) {
  return usefulConcerts(hub).length > 0;
}

export function hasPersonSidebar(hub) {
  return hasCuriosities(hub) || hasNews(hub) || hasPersonImage(hub);
}

export function hasPersonImage(hub) {
  return nonEmptyString(hub?.entity?.imageUrl);
}

/**
 * Person "hero panel" needs real media/platforms/next event — not name alone.
 */
export function hasPersonPortraitPanel(hub) {
  return (
    hasPersonImage(hub) ||
    hasStreamingPlatforms(hub) ||
    hasNextEvent(hub)
  );
}

export function hasPersonHero(hub) {
  return hasPersonPortraitPanel(hub) || hasLinks(hub);
}

function hasVisualNextEvent(hub) {
  const event = hub?.nextEvent;
  if (!event || typeof event !== 'object') return false;
  return Boolean(
    nonEmptyString(event.venueImageUrl) ||
      nonEmptyString(event.home?.crestUrl) ||
      nonEmptyString(event.away?.crestUrl) ||
      nonEmptyString(event.competition?.badgeUrl)
  );
}

/**
 * Sports hub needs standings and/or a next event with real imagery.
 * Text-only fixtures (no crests / venue / badge) are too sparse — hide the hub.
 */
function hasMeaningfulSportsContent(hub) {
  if (hasStandings(hub)) return true;
  if (hasNextEvent(hub) && hasVisualNextEvent(hub)) return true;
  const type = String(hub?.entity?.type || '').trim().toLowerCase();
  // Club search with only related fixtures still needs the club card.
  if (['club', 'team'].includes(type) && (hasPersonImage(hub) || hasLinks(hub))) {
    return true;
  }
  return false;
}

/**
 * Person hub needs a real identity surface (image, streaming chips, next event,
 * or official links). Curiosities/news alone must not open an empty two-column hub.
 */
function hasMeaningfulPersonContent(hub) {
  return hasPersonPortraitPanel(hub) || hasLinks(hub);
}

/**
 * Whether the hub should render at all for this payload.
 * `hubRenderable: false` always hides. `true` still requires meaningful sections
 * so empty portraits / fallback curiosities do not paint an empty hub.
 */
export function isHubRenderable(hub) {
  if (!hub || typeof hub !== 'object') return false;
  if (hub.hubRenderable === false) return false;

  const family = hub.entity?.hubFamily || hub.hubFamily;
  if (family === 'sports') return hasMeaningfulSportsContent(hub);
  if (family === 'person') return hasMeaningfulPersonContent(hub);

  return hasMeaningfulSportsContent(hub) || hasMeaningfulPersonContent(hub);
}

export function getHubFamily(hub) {
  return hub?.entity?.hubFamily || hub?.hubFamily || null;
}
